import React, { useCallback, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Eye, Trash2, MapPin, Truck, Edit, AlertTriangle } from 'lucide-react';
import { Button } from '@components/Button';
import { Input } from '@components/Input';
import { Card, CardContent } from '@components/Card';
import { SimpleTable as Table, TableHeader, TableRow, TableCell } from '@components/Table';
import { Badge } from '@components/Card';
import { PermissionGuard } from '@components/PermissionGuard';
import { driverRouteService } from '@services/driverRouteService';
import { DriverRoute } from '@/types/driverRoute';
import { parseOrderIdSearch } from './driverRouteUtils';

const formatDate = (dateStr?: string) => {
  if (!dateStr) return '—';
  // Strip Z/offset: backend returns Brasília local time, no UTC conversion should occur
  try { return new Date(dateStr.replace(/Z$|[+-]\d{2}:\d{2}$/, '')).toLocaleDateString('pt-BR'); } catch { return dateStr; }
};

const formatDateTime = (dateStr?: string | null) => {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr.replace(/Z$|[+-]\d{2}:\d{2}$/, '')).toLocaleString('pt-BR', {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch { return dateStr; }
};

/** Filtros já confirmados pelo botão Buscar (o que de fato vai para a API). */
interface AppliedFilters {
  driverName?: string;
  orderId?: number;
  customerName?: string;
}

export const DriverRouteListView: React.FC = () => {
  const navigate = useNavigate();

  const [items, setItems] = useState<DriverRoute[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [orderIdSearch, setOrderIdSearch] = useState('');
  const [customerSearch, setCustomerSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);
  // Filtros JÁ APLICADOS (commit do botão Buscar), separados do que está digitado:
  // é deles que a consulta depende, então trocar de página não pega meia digitação
  // e Buscar/Limpar sempre disparam uma nova consulta.
  const [applied, setApplied] = useState<AppliedFilters>({});
  const pageSize = 20;

  const loadItems = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await driverRouteService.getAll({
        page: currentPage,
        pageSize,
        searchName: applied.driverName,
        order_id: applied.orderId,
        customer_name: applied.customerName,
      });
      setItems(result.data);
      setTotalPages(result.pagination.totalPages);
      setTotalItems(result.pagination.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  }, [currentPage, applied]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  // A coluna das paradas encontradas só existe quando a consulta foi por pedido/cliente.
  const hasStopFilter = applied.orderId !== undefined || applied.customerName !== undefined;

  const handleSearch = () => {
    setCurrentPage(1);
    setApplied({
      driverName: search.trim() || undefined,
      orderId: parseOrderIdSearch(orderIdSearch),
      customerName: customerSearch.trim() || undefined,
    });
  };

  const handleClearSearch = () => {
    setSearch('');
    setOrderIdSearch('');
    setCustomerSearch('');
    setCurrentPage(1);
    setApplied({});
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
  };

  const handleDelete = async (id: number) => {
    try {
      await driverRouteService.delete(id);
      setDeleteConfirm(null);
      loadItems();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao excluir');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-h1">Gerenciar Rotas</h1>
          <p className="text-slate-600">Crie e gerencie as rotas de entrega e retirada dos motoristas</p>
        </div>
        <PermissionGuard permission="driver_routes.write">
          <Button onClick={() => navigate('/driver-routes/new')} icon={<Plus size={20} />}>
            Nova Rota
          </Button>
        </PermissionGuard>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="p-4 space-y-2">
          <div className="flex flex-col sm:flex-row gap-2">
            <Input
              placeholder="Motorista..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={handleKeyDown}
              className="flex-1"
            />
            <Input
              placeholder="Pedido de venda (nº)..."
              value={orderIdSearch}
              onChange={(e) => setOrderIdSearch(e.target.value)}
              onKeyDown={handleKeyDown}
              className="flex-1"
            />
            <Input
              placeholder="Cliente..."
              value={customerSearch}
              onChange={(e) => setCustomerSearch(e.target.value)}
              onKeyDown={handleKeyDown}
              className="flex-1"
            />
            <Button onClick={handleSearch} variant="secondary" icon={<Search size={18} />}>
              Buscar
            </Button>
            {(search || orderIdSearch || customerSearch) && (
              <Button onClick={handleClearSearch} variant="outline">
                Limpar
              </Button>
            )}
          </div>
          <p className="text-xs text-slate-500">
            Busque por pedido ou cliente para descobrir em qual rota a entrega foi feita — a rota
            aparece com a parada correspondente e a data da entrega.
          </p>
        </CardContent>
      </Card>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">{error}</div>
      )}

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <tr>
                <th className="text-left">ID</th>
                <th className="text-left">Motorista</th>
                <th className="text-left">Veículo</th>
                <th className="text-center">Paradas</th>
                <th className="text-center">Pendentes</th>
                <th className="text-left">Data</th>
                <th className="text-center">Status</th>
                {hasStopFilter && <th className="text-left">Parada encontrada</th>}
                <th className="text-center">Ações</th>
              </tr>
            </TableHeader>
            <tbody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={hasStopFilter ? 9 : 8} className="text-center py-8 text-slate-500">
                    Carregando...
                  </TableCell>
                </TableRow>
              ) : items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={hasStopFilter ? 9 : 8} className="text-center py-8 text-slate-500">
                    Nenhuma rota encontrada
                  </TableCell>
                </TableRow>
              ) : (
                items.map((item) => {
                  const pending = item.pending_stops_count ?? 0;
                  const total = item.total_stops_count ?? item.stops?.length ?? 0;
                  const allDelivered = pending === 0 && total > 0;

                  return (
                    <TableRow key={item.driver_route_id}>
                      <TableCell className="font-mono text-sm">{item.driver_route_id}</TableCell>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          <Truck size={16} className="text-slate-400" />
                          {item.driver?.full_name || `Motorista #${item.driver_id}`}
                        </div>
                      </TableCell>
                      <TableCell>{item.vehicle?.description || `Veículo #${item.vehicle_id}`}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <MapPin size={14} className="text-slate-400" />
                          {total}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={pending > 0 ? 'warning' : 'success'}>
                          {pending}
                        </Badge>
                      </TableCell>
                      <TableCell>{formatDate(item.created_at)}</TableCell>
                      <TableCell>
                        <Badge variant={allDelivered ? 'success' : 'warning'}>
                          {allDelivered ? 'Concluída' : 'Pendente'}
                        </Badge>
                        {item.driver_not_follow_route && (
                          <span
                            className="inline-flex items-center gap-1 ml-2 text-xs font-medium text-red-700 bg-red-50 border border-red-200 px-2 py-0.5 rounded-full"
                            title="O motorista não seguiu a sequência das paradas planejadas"
                          >
                            <AlertTriangle size={12} /> Fora de rota
                          </span>
                        )}
                      </TableCell>
                      {hasStopFilter && (
                        <TableCell>
                          {(item.matched_stops ?? []).length === 0 ? (
                            <span className="text-slate-400">—</span>
                          ) : (
                            <div className="space-y-1">
                              {(item.matched_stops ?? []).map((stop) => (
                                <div key={stop.driver_route_stop_id} className="text-sm">
                                  <span className="font-medium text-slate-700">
                                    {stop.order_id ? `Pedido #${stop.order_id}` : `Parada #${stop.driver_route_stop_id}`}
                                  </span>
                                  {stop.customer_name && (
                                    <span className="text-slate-500"> — {stop.customer_name}</span>
                                  )}
                                  <div className="text-xs">
                                    {stop.is_delivered ? (
                                      <span className="text-emerald-700">
                                        Entregue em {formatDateTime(stop.delivered_at)}
                                      </span>
                                    ) : (
                                      <span className="text-amber-700">Entrega pendente</span>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </TableCell>
                      )}
                      <TableCell>
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => navigate(`/driver-routes/${item.driver_route_id}`)}
                            icon={<Eye size={16} />}
                          />
                          <PermissionGuard permission="driver_routes.write">
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={() => navigate(`/driver-routes/${item.driver_route_id}/edit`)}
                              icon={<Edit size={16} />}
                            />
                          </PermissionGuard>
                          <PermissionGuard permission="driver_routes.write">
                            {deleteConfirm === item.driver_route_id ? (
                              <div className="flex items-center gap-1">
                                <Button variant="danger" size="sm" onClick={() => handleDelete(item.driver_route_id)}>
                                  Confirmar
                                </Button>
                                <Button variant="secondary" size="sm" onClick={() => setDeleteConfirm(null)}>
                                  Cancelar
                                </Button>
                              </div>
                            ) : (
                              <Button
                                variant="secondary"
                                size="sm"
                                onClick={() => setDeleteConfirm(item.driver_route_id)}
                                icon={<Trash2 size={16} className="text-red-500" />}
                              />
                            )}
                          </PermissionGuard>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </tbody>
          </Table>
        </CardContent>
      </Card>

      {/* Paginação — sempre visível quando há resultado: é por ela que se chega às
          rotas mais antigas (a listagem vem ordenada da mais recente para a mais antiga). */}
      {totalItems > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-sm text-slate-600">{totalItems} registro(s) encontrado(s)</p>
          <div className="flex gap-2">
            <Button variant="secondary" size="sm" disabled={currentPage <= 1} onClick={() => setCurrentPage(1)}>
              Primeira
            </Button>
            <Button variant="secondary" size="sm" disabled={currentPage <= 1} onClick={() => setCurrentPage((p) => p - 1)}>
              Anterior
            </Button>
            <span className="flex items-center px-3 text-sm text-slate-600">Página {currentPage} de {totalPages}</span>
            <Button variant="secondary" size="sm" disabled={currentPage >= totalPages} onClick={() => setCurrentPage((p) => p + 1)}>
              Próxima
            </Button>
            <Button variant="secondary" size="sm" disabled={currentPage >= totalPages} onClick={() => setCurrentPage(totalPages)}>
              Última
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
